import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router';
import { Button, Icon } from 'semantic-ui-react';
import { FormattedDate } from 'react-intl';
import { MotifIconButton, MotifIcon, MotifTextLink } from '@ey-xd/motif-react';
import {
  navigationIcRefresh24px,
  contentIcUndo24px,
  communicationIcEmail24px,
  actionIcCheckCircle24px,
} from '@ey-xd/motif-react/assets/icons';
import { IconoirTrash, IconoirOpenNewWindow } from '@ey-xd/motif-icon';

const PsqChecklistSummaryItem = ({
  selected,
  checklist,
  handleSelectedChecklistChanged,
  handleSendNotification,
  previousChecklist,
  handleRevertChecklistState,
  getCheckListItemStateName,
  isEngagementAdminOrSysAdmin,
  handleDelete,
  canDelete,
  isRevertStateVisible,
  discrepancyCount,
  getPsqChecklistLink,
  lookupTypes,
}) => {
  handleSelectedChecklistChanged = handleSelectedChecklistChanged.bind(
    this,
    checklist,
    previousChecklist
  );
  handleSendNotification = handleSendNotification.bind(this, checklist);
  handleDelete = handleDelete.bind(this, checklist);

  let propertyType = checklist.propertyType;
  const propertyTypeObject = lookupTypes.propertyTypes.find(
    (pt) => pt.propertyTypeID === checklist.propertyType
  );
  if (propertyTypeObject != undefined && propertyTypeObject != null) {
    propertyType =
      propertyTypeObject.propertyTypeDescription === 'Not Selected'
        ? ''
        : propertyTypeObject.propertyTypeDescription;
  }

  let className = '';
  if (selected) {
    className = 'selected';
  }

  let stateName = getCheckListItemStateName(checklist.checklistStateID);

  let revertChecklistStateDisabled = false;
  if (stateName == 'Created') {
    revertChecklistStateDisabled = true;
  }

  return (
    <tr key={checklist.checklistID} className={className}>
      <td style={{ textAlign: 'center' }}>
        {(selected && <span>&nbsp;</span>) || (
          <MotifIconButton
            aria-label="Select this PSQ"
            type="button"
            onClick={handleSelectedChecklistChanged}
          >
            <MotifIcon src={actionIcCheckCircle24px} title="Select this PSQ" />
          </MotifIconButton>
        )}
      </td>
      <td>
        <div className="ag-cell" style={{ height: 'auto' }}>
          <Link
            tabIndex="-1"
            to={getPsqChecklistLink(checklist.checklistID)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <MotifIcon
              className="linkIcon"
              iconFunction={IconoirOpenNewWindow}
              size="24"
            />
          </Link>
        </div>
      </td>
      <td>{checklist.propertyName}</td>
      <td>{propertyType}</td>
      <td>{checklist.questionnaireTemplateName}</td>
      <td>{checklist.propertyManagerName}</td>
      <td>
        {(checklist.sentDate && (
          <FormattedDate value={checklist.sentDate} />
        )) || <span>&nbsp;</span>}
      </td>
      <td>
        {(checklist.createdDate && (
          <FormattedDate value={checklist.createdDate} />
        )) || <span>&nbsp;</span>}
      </td>
      <td>
        {stateName === 'Sent' ? 'Email Sent' : stateName}
        {(discrepancyCount > 0 && (
          <span className="ml-1 badge badge-warning badgeStandard">
            {discrepancyCount}
          </span>
        )) ||
          null}
      </td>
      <td>
        {(checklist.reviewCompletedDate && (
          <FormattedDate value={checklist.reviewCompletedDate} />
        )) || <span>&nbsp;</span>}
      </td>
      <td style={{ textAlign: 'center' }}>
        {(selected &&
          (stateName.toLowerCase() === 'created' ||
            stateName.toLowerCase() === 'in progress') && (
            <MotifIconButton
              aria-label="Send this PSQ"
              type="button"
              onClick={handleSendNotification}
            >
              <MotifIcon src={communicationIcEmail24px} title="Send this PSQ" />
            </MotifIconButton>
          )) || <span>&nbsp;</span>}
      </td>
      {isRevertStateVisible ? (
        <td style={{ textAlign: 'center' }}>
          {(selected && (
            <MotifIconButton
              aria-label="Revert PSQ State"
              type="button"
              disabled={revertChecklistStateDisabled}
              onClick={handleRevertChecklistState}
            >
              <MotifIcon
                src={contentIcUndo24px}
                title={revertChecklistStateDisabled ? '' : 'Revert PSQ State'}
              />
            </MotifIconButton>
          )) || <span>&nbsp;</span>}
        </td>
      ) : null}
      {(canDelete && (
        <td style={{ textAlign: 'center' }}>
          <MotifIconButton
            aria-label="Delete this PSQ"
            type="button"
            onClick={handleDelete}
          >
            <MotifIcon
              iconFunction={IconoirTrash}
              size="24"
              title="Delete this PSQ"
            />
          </MotifIconButton>
        </td>
      )) ||
        null}
    </tr>
  );
};

PsqChecklistSummaryItem.propTypes = {
  selected: PropTypes.bool,
  checklist: PropTypes.object,
  handleSelectedChecklistChanged: PropTypes.func,
  handleSendNotification: PropTypes.func,
  previousChecklist: PropTypes.object,
  handleRevertChecklistState: PropTypes.func,
  getCheckListItemStateName: PropTypes.func,
  isEngagementAdminOrSysAdmin: PropTypes.bool,
  handleDelete: PropTypes.func,
  canDelete: PropTypes.bool,
  isRevertStateVisible: PropTypes.bool,
  discrepancyCount: PropTypes.number,
  getPsqChecklistLink: PropTypes.func,
};

const PsqChecklistSummary = ({
  activeChecklist,
  previousChecklist,
  checklists,
  handleSelectedChecklistChanged,
  handleSendNotification,
  handleRevertChecklistState,
  getCheckListItemStateName,
  isEngagementAdminOrSysAdmin,
  handleDelete,
  canDelete,
  canRevertState,
  getPsqChecklistLink,
  lookupTypes,
}) => {
  if (
    checklists === undefined ||
    checklists === null ||
    checklists.length === 0
  ) {
    return <div>There are no checklists to display</div>;
  }

  let isRevertStateVisible = false;
  if (isEngagementAdminOrSysAdmin && canRevertState) {
    isRevertStateVisible = true;
  }

  return (
    <div>
      <div className="row">
        <div className="col">
          <span className="pageheader">
            Property Survey Questionnaire Checklist
          </span>
        </div>
      </div>
      <div className="row">
        <div className="col table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col" style={{ width: '1%' }}>
                  Select
                </th>
                <th scope="col" style={{ width: '3%' }}></th>
                <th scope="col">Property Name</th>
                <th scope="col">Property Type</th>
                <th scope="col">Custom Template</th>
                <th scope="col">Property Manager</th>
                <th scope="col">Date Sent</th>
                <th scope="col">Date Created</th>
                <th scope="col">Status</th>
                <th scope="col">Review Completed</th>
                <th scope="col" style={{ textAlign: 'center' }}>
                  Send
                </th>
                {isRevertStateVisible ? (
                  <th scope="col" style={{ textAlign: 'center' }}>
                    Revert State
                  </th>
                ) : null}
                {(canDelete && (
                  <th scope="col" style={{ textAlign: 'center' }}>
                    Delete
                  </th>
                )) ||
                  null}
              </tr>
            </thead>
            <tbody>
              {checklists.map((checklist) => (
                <PsqChecklistSummaryItem
                  key={checklist.checklistID}
                  selected={
                    activeChecklist &&
                    checklist.checklistID === activeChecklist.checklistID
                  }
                  checklist={checklist}
                  handleSendNotification={handleSendNotification}
                  handleSelectedChecklistChanged={
                    handleSelectedChecklistChanged
                  }
                  previousChecklist={previousChecklist}
                  handleRevertChecklistState={handleRevertChecklistState}
                  getCheckListItemStateName={getCheckListItemStateName}
                  isEngagementAdminOrSysAdmin={isEngagementAdminOrSysAdmin}
                  handleDelete={handleDelete}
                  canDelete={canDelete}
                  isRevertStateVisible={isRevertStateVisible}
                  discrepancyCount={checklist.discrepancyCount}
                  getPsqChecklistLink={getPsqChecklistLink}
                  lookupTypes={lookupTypes}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

PsqChecklistSummary.propTypes = {
  checklists: PropTypes.array,
  activeChecklist: PropTypes.object,
  previousChecklist: PropTypes.object,
  handleSelectedChecklistChanged: PropTypes.func,
  handleSendNotification: PropTypes.func,
  handleRevertChecklistState: PropTypes.func,
  getCheckListItemStateName: PropTypes.func,
  isEngagementAdminOrSysAdmin: PropTypes.bool,
  handleDelete: PropTypes.func,
  canDelete: PropTypes.bool,
  canRevertState: PropTypes.bool,
  getPsqChecklistLink: PropTypes.func,
};

export default PsqChecklistSummary;
